import { useEffect, useMemo, useState } from 'react';
import type { AudiencePush, TrackedCohort, TrackedRequest } from '../../types';
import { ApiError, api } from '../../api/client';
import { Button, Field, Input, Notice, Spinner, cx } from '../ui/primitives';
import { Modal } from '../ui/Modal';
import { Pill, formatDateTime } from './shared';

/**
 * Recording what was actually pushed, for one tab.
 *
 * Audiences go live in batches — three of six cohorts today, the rest next week —
 * and a cohort can be pushed *again* later: a top-up in September against the
 * audience first pushed in August. So each cohort holds a list of pushes, and the
 * form's first job is to show which are already live.
 */

/** True when a push has anything worth saving typed against it. */
function isFilled(push: AudiencePush): boolean {
  return Boolean(push.audienceName.trim() || push.audienceCount.trim() || push.pushedOn.trim());
}

/** A blank push for a cohort, with an id the server can match on later. */
function blankPush(cohortName: string): AudiencePush {
  return {
    // Unique within this request, which is all the server needs to tell the
    // pushes of one cohort apart when the form is saved again.
    id: `push-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    cohortName,
    audienceName: '',
    audienceCount: '',
    pushedOn: '',
    recordedAt: '',
  };
}

/**
 * The per-cohort form.
 *
 * Audiences are pushed in batches — three of six cohorts today, the rest next
 * week — and a cohort can be pushed *again* later: a top-up in September against
 * the audience first pushed in August. So each cohort holds a list of pushes,
 * each with its own name, size and date, and the form's first job is to show
 * which are already live.
 *
 * Everything stays editable: a recorded push can be corrected, and saving
 * rewrites the whole table in the document.
 */
export function AudiencePushForm({
  entry,
  onClose,
  onSaved,
}: {
  entry: TrackedRequest;
  onClose: () => void;
  onSaved: (updated: TrackedRequest, note: string) => void;
}) {
  const [cohorts, setCohorts] = useState<TrackedCohort[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const detail = await api.getRequest(entry.id);
        if (cancelled) return;

        // A cohort with nothing recorded still needs a row to type into.
        setCohorts(
          detail.cohorts.map((cohort) => ({
            ...cohort,
            pushes: cohort.pushes.length ? cohort.pushes : [blankPush(cohort.name)],
          })),
        );
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : 'The request could not be loaded.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entry.id]);

  const updatePush = (cohortIndex: number, pushId: string, changes: Partial<AudiencePush>) => {
    setCohorts((current) =>
      current
        ? current.map((cohort, index) =>
            index === cohortIndex
              ? {
                  ...cohort,
                  pushes: cohort.pushes.map((push) =>
                    push.id === pushId ? { ...push, ...changes } : push,
                  ),
                }
              : cohort,
          )
        : current,
    );
  };

  const addPush = (cohortIndex: number) => {
    setCohorts((current) =>
      current
        ? current.map((cohort, index) =>
            index === cohortIndex
              ? { ...cohort, pushes: [...cohort.pushes, blankPush(cohort.name)] }
              : cohort,
          )
        : current,
    );
  };

  const removePush = (cohortIndex: number, pushId: string) => {
    setCohorts((current) =>
      current
        ? current.map((cohort, index) => {
            if (index !== cohortIndex) return cohort;
            const remaining = cohort.pushes.filter((push) => push.id !== pushId);
            // Never leave a cohort with no row at all to type into.
            return { ...cohort, pushes: remaining.length ? remaining : [blankPush(cohort.name)] };
          })
        : current,
    );
  };

  const save = async () => {
    if (!cohorts) return;

    setSaving(true);
    setSaveError(null);

    try {
      const result = await api.recordAudiencePush(
        entry.id,
        cohorts.flatMap((cohort) =>
          cohort.pushes
            // An untouched blank row is not a push; sending it would only add an
            // empty row to the document.
            .filter((push) => isFilled(push))
            .map((push) => ({ ...push, cohortName: cohort.name })),
        ),
      );

      /*
       * The save and the document write are reported separately on purpose: the
       * details are stored either way, and pretending otherwise would leave the
       * operator retyping work that was never lost.
       */
      const added = result.rowsAdded ? `, ${result.rowsAdded} new row(s) added` : '';
      const note = result.documentUpdated
        ? result.appended
          ? 'Saved. The document had no push table, so one was added at the end of the tab.'
          : `Saved and written into the document (${result.rowsWritten} row(s)${added}).`
        : `Saved, but the document was not updated. ${result.documentError}`;

      onSaved(result.request, note);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'The details could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    const all = (cohorts ?? []).flatMap((cohort) => cohort.pushes);
    return {
      pushed: (cohorts ?? []).filter((cohort) => cohort.pushes.some((push) => push.recordedAt))
        .length,
      cohorts: cohorts?.length ?? 0,
      recorded: all.filter((push) => push.recordedAt).length,
    };
  }, [cohorts]);

  const readyToSave = (cohorts ?? []).some((cohort) => cohort.pushes.some(isFilled));

  const pushCard = (cohort: TrackedCohort, cohortIndex: number, push: AudiencePush, order: number) => {
    const recorded = Boolean(push.recordedAt);
    const extra = order > 0;

    return (
      <div
        key={push.id}
        className={cx(
          'rounded-input border p-3.5',
          recorded ? 'border-success/30 bg-success/[0.04]' : 'border-line bg-surface',
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-micro font-medium text-ink-muted">
            {extra ? `Additional push ${order + 1}` : 'First push'}
          </span>

          <div className="flex items-center gap-2">
            <Pill tone={recorded ? 'done' : 'none'}>{recorded ? 'Pushed' : 'Not pushed'}</Pill>
            {cohort.pushes.length > 1 ? (
              <Button
                variant="ghost"
                className="px-2 py-1"
                onClick={() => removePush(cohortIndex, push.id)}
              >
                Remove
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1.4fr_1fr_1fr]">
          <Field label="Audience Name" htmlFor={`audience-name-${push.id}`}>
            <Input
              id={`audience-name-${push.id}`}
              value={push.audienceName}
              placeholder="As named in the channel"
              onChange={(event) =>
                updatePush(cohortIndex, push.id, { audienceName: event.target.value })
              }
            />
            {/*
              The recorded date sits under the name because that is the field the
              operator scans to recognise an audience they already created.
            */}
            {recorded ? (
              <p className="mt-1 text-micro text-ink-faint">
                Last updated {formatDateTime(push.recordedAt)}
              </p>
            ) : null}
          </Field>

          <Field label="No. Pushed" htmlFor={`audience-count-${push.id}`}>
            <Input
              id={`audience-count-${push.id}`}
              value={push.audienceCount}
              placeholder="e.g. 8 - 10 mn"
              onChange={(event) =>
                updatePush(cohortIndex, push.id, { audienceCount: event.target.value })
              }
            />
          </Field>

          <Field label="Date Pushed" htmlFor={`pushed-on-${push.id}`}>
            <Input
              id={`pushed-on-${push.id}`}
              type="date"
              value={push.pushedOn}
              onChange={(event) =>
                updatePush(cohortIndex, push.id, { pushedOn: event.target.value })
              }
            />
          </Field>
        </div>
      </div>
    );
  };

  const cohortCard = (cohort: TrackedCohort, cohortIndex: number) => {
    const anyRecorded = cohort.pushes.some((push) => push.recordedAt);

    return (
      <section
        key={`${cohort.name}-${cohortIndex}`}
        className="rounded-card border border-line bg-surface-muted/40 p-4"
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-micro font-medium uppercase tracking-wide text-ink-faint">
              Cohort {cohortIndex + 1}
            </p>
            <p className="mt-0.5 text-field font-medium text-ink">
              {cohort.name || 'Unnamed cohort'}
            </p>
            {cohort.size ? (
              <p className="mt-0.5 text-micro text-ink-muted">Brief size: {cohort.size}</p>
            ) : null}
          </div>

          <Pill tone={anyRecorded ? 'done' : 'none'}>
            {cohort.pushes.filter((push) => push.recordedAt).length} pushed
          </Pill>
        </div>

        <div className="mt-3 space-y-2.5">
          {cohort.pushes.map((push, order) => pushCard(cohort, cohortIndex, push, order))}
        </div>

        <div className="mt-3">
          <Button variant="secondary" className="px-3 py-1.5" onClick={() => addPush(cohortIndex)}>
            Add another push
          </Button>
        </div>
      </section>
    );
  };

  return (
    <Modal
      open
      title="Record audience push"
      description={`${entry.brandName} · ${entry.tabTitle}`}
      width="xl"
      onClose={onClose}
      footer={
        cohorts?.length ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving} disabled={!readyToSave}>
              Save and update document
            </Button>
          </>
        ) : null
      }
    >
      {loadError ? (
        <Notice tone="error" title="Could not load the cohorts">
          {loadError}
        </Notice>
      ) : !cohorts ? (
        <div className="flex items-center gap-2.5 py-6 text-field text-ink-muted">
          <Spinner />
          Loading cohorts…
        </div>
      ) : !cohorts.length ? (
        <Notice tone="info" title="No cohorts on this request">
          Nothing was ticked when this rollout was created, so there is nothing to record against.
        </Notice>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 text-micro text-ink-muted">
            <Pill tone={totals.pushed ? 'done' : 'none'}>
              {totals.pushed} of {totals.cohorts} cohorts pushed
            </Pill>
            {totals.recorded > totals.pushed ? (
              <span>{totals.recorded} pushes recorded in total</span>
            ) : null}
            {entry.pushRecordedAt ? (
              <span>Last recorded {formatDateTime(entry.pushRecordedAt)}</span>
            ) : null}
          </div>

          {cohorts.map(cohortCard)}

          {saveError ? (
            <Notice tone="error" title="Could not save">
              {saveError}
            </Notice>
          ) : null}

          <p className="text-micro text-ink-muted">
            A cohort you have not pushed yet can be left blank and recorded later. Use
            <span className="text-ink"> Add another push</span> when the same cohort is pushed again
            with a different audience or size.
          </p>
        </div>
      )}
    </Modal>
  );
}
